import Phaser from 'phaser';
import { STREET_SPAWNS } from '../config.js';
import { gameState } from '../state/gameState.js';
import { messageFor } from '../api/errors.js';
import { setCarOccupants, createCarSprite, fitCarHitArea, setCarFacing, setCarGroundY } from './CarSprite.js';
import { CarHud } from './CarHud.js';
import {
  HOUSE_PARK_RANGE, LANE_CHANGE_MS, MISFUEL_DIE_AFTER_M, NEAR, PUMPS, PUMP_RANGE,
  PX_PER_KMH, PX_PER_M, ROAD_WHEEL_Y, UTURN_MS, carLaneY, wheelYForLane,
} from './carCatalog.mjs';
import { createDriveState, fuelBurn, pxPerSec, regen, step } from './carPhysics.mjs';
import { showFuelModal } from './FuelModal.js';
import { litres } from './fuelOptions.mjs';
import { isInputLocked } from './inputLock.js';
import { correctLane, laneChangeAllowed } from './laneLogic.mjs';
import { showActionsModal, showModal } from './Modal.js';
import {
  SCHOOL_CAR_MODEL, abortMenu, isSchoolCarId, schoolCarRecord,
} from './schoolCar.mjs';
import { showToast } from './Toast.js';
import { UTURN_LABEL, UturnButton } from './UturnButton.js';
import { LABEL_DEPTH } from './ySort.js';
import { virtualInput } from './virtualInput.js';
import { iconText } from './iconText.js';

// Everything about YOUR cars on the street: the ones you left standing at the kerb, the
// one you are driving, and the paperwork that keeps the server's odometer and fuel tank in
// step with the drive. Other people's cars are not our business — attachVehiclePresence
// draws those (and skips ours, so the two never draw the same car twice).
//
//   const car = new CarController(scene, {
//     natalka, presence,
//     onEnter: (sprite) => scene._followWithLag(sprite),
//     onExit:  (natalka) => scene._followWithLag(natalka),
//     onBroadcast: (p) => realtime.move(p),
//     onKick: () => realtime.carKick(),
//     canDrive: () => !scene.isAPassenger,   // false while riding in someone else's car
//     worldWidth: WORLD_WIDTH,
//     onAbortLesson: () => scene.abortLesson(),   // the school car's only menu row
//     parked: false,   // a scene with no street to park on (the training route)
//     speedLimit: STREET_LIMIT_KMH,   // the sign the HUD shows for the whole scene
//   });
//
// The driving school's Yaris („L") rides along in here too — `startSchoolCar()`. It is not
// a car of yours: no fuel burn, no drive-reports, no pump, no parking. See schoolCar.mjs.
//
// The scene must: call `car.update(dt)` instead of its walker while `car.isDriving`,
// call `car.syncAction()` every frame, `car.onState()` on a gameState emit, and gate its
// door/edge exits on `!car.isDriving`.

// Steering. Only the horizontal axis matters: the road runs left–right.
const AXIS_THRESHOLD = 0.4;   // joystick deflection that counts as a key press
const CREEP_KMH = 3;          // below this the "other" key means reverse, not brake
const REVERSE_HOLD_MS = 150;  // opposite key held this long at a standstill = reverse

// Lanes. Up/down is a single press, not a steer: one press glides the car across to the
// other lane and that is the whole gesture. The joystick wants a deliberate shove (the
// steering axis only needs 0.4, but a thumb pushing left inevitably drifts up or down).
const LANE_AXIS = 0.6;

// Reversing bleeps once a second, like a lorry, so a child who leant on the arrow key can
// HEAR that the car is going the other way.
const BEEP_EVERY_MS = 1000;

// Pitch: the body dives under braking and squats under power (degrees, 250 ms tween).
const PITCH_BRAKE = -2, PITCH_THROTTLE = 1.5, PITCH_MS = 250;

const REPORT_EVERY_MS = 30_000;   // odometer/fuel sync cadence while driving
const BROADCAST_MS = 100;         // 10 Hz, same as the walker

const EDGE_MARGIN = 60;           // how close to the world edge the car may get

// The wrong fuel. The engine keeps running — badly — for MISFUEL_DIE_AFTER_M metres:
// the throttle only half arrives (a fresh fraction every SPUTTER_MS) and the body shakes
// on its mounts. Then it dies, and the car rolls to a stop within DIE_BRAKE_MS.
const SPUTTER_MS = 200;
const SPUTTER_MIN = 0.3;         // worst fraction of the pedal that reaches the wheels
const SPUTTER_JITTER_PX = 2;
const DIE_BRAKE_MS = 1500;

// Where the driver ends up when she gets out: clear of the bodywork (half the car plus
// this gap) and one step up the road from the lane, so she never sorts inside the car.
const EXIT_GAP = 40;
const EXIT_Y_LIFT = 40;

// Labels are what the mobile action button shows, so they double as the ownership guard:
// only ever clear the shared virtual action when the label on it is one of ours.
const LABEL_ENTER = 'Wsiądź {car}';
const LABEL_DRIVE = 'Auto {car}';
const LABEL_FUEL = 'Zatankuj {fuel}';
const OUR_LABELS = new Set([LABEL_ENTER, LABEL_DRIVE, LABEL_FUEL]);

// Nobody may be left in the car when the driver walks away, so the menu grows a row for
// throwing them out (the server's `car_kick`, which empties every car this player drives).
const LABEL_KICK = 'Wyproś pasażerów {friends}';

const clamp = (n, lo, hi) => (n < lo ? lo : n > hi ? hi : n);
const facingStr = (f) => (f < 0 ? 'left' : 'right');
const myCharacter = () => (typeof window !== 'undefined' ? window.__myCharacter : null) ?? 'natalka';

export class CarController {
  constructor(scene, { natalka, presence = null, onBroadcast = null, onEnter = null, onExit = null,
    onKick = null, onAbortLesson = null, canDrive = null, worldWidth, parked = true,
    walkY = null, speedLimit = null } = {}) {
    this.scene = scene;
    this.natalka = natalka;
    virtualInput.wantStickFor(scene, this);   // steering on phones
    this.presence = presence;         // the room's remote avatars (passengers read it)
    this._onBroadcast = onBroadcast;
    this._onEnter = onEnter;
    this._onExit = onExit;
    this._onKick = onKick;
    // Breaking off a lesson is the SCENE's business (which endpoint, which toast, where the
    // character ends up) — the controller only offers the row and asks "are you sure?".
    this._onAbortLesson = onAbortLesson;
    // A passenger in someone else's car must not be able to get into one of their own —
    // the street says so, the same way it gates the bike (see BikeController's canRide).
    this._canDrive = canDrive ?? (() => true);
    // Where the driver's FEET belong once she climbs out. On the street that is the pavement,
    // which is nowhere near the lane the car is standing in; scenes with no pavement (the
    // training route, where nobody ever gets out) fall back to a step up the road.
    this._walkY = walkY;
    // The speed limit that applies on this scene by default — the street's own 50. A route
    // overrides it per segment with `setSpeedLimit()`; null means no sign at all.
    this._speedLimit = speedLimit;
    this.worldWidth = worldWidth ?? 6395;

    this.car = null;      // the state record of the car we're in, null while on foot
    this.sprite = null;   // its Container while driving
    this.drive = null;    // carPhysics state

    this.fuelLocal = 0;         // litres/kWh, ours to spend until the next report lands
    this.metersThisTrip = 0;    // for the odometer readout
    this._odometerBase = 0;     // the server's odometer as it stood when this drive began
    this._metersSinceReport = 0;
    this._reportTimer = 0;
    this._reportsStopped = false;   // a rejected report kills reporting for this drive
    this._resyncFuel = false;       // …and re-reads the tank from the next state snapshot

    // Your own cars standing on this street are drawn as scenery — except where there is no
    // street to stand on: the training route (TrainingRouteScene) is its own world, and the
    // `street` cars parked at whatever x would otherwise turn up in the middle of a lesson.
    this._showParked = parked;
    this._parked = new Map();   // car_id → Container, our cars standing on this street
    this._oppDownSince = null;  // when the "wrong way" key went down (tap vs hold)
    this._oppMode = null;       // 'brake' | 'turn' — what that press meant when it started
    this._prevAxis = 0;         // last frame's steering axis, so we can spot a rising edge
    this._pitch = 0;
    this._lastBroadcast = 0;
    this._wasMoving = false;
    this._busy = false;         // an await on park/refuel/rescue is in flight
    this._passengers = 0;       // riders aboard, straight from the server's `car_passengers`
    this._riderChars = [];       // …and who they are, drawn in the windows behind me
    this._riderLooks = [];

    this._lesson = null;        // the lesson/exam we're driving for, while in the school car
    this._inputLocked = false;  // the route director froze the wheel (finish line, result)
    this._uturned = false;      // a U-turn began during the update() that just ran
    this._uturnTween = null;    // …and the tween that swings the body into the other lane

    // Lanes. `lane` is the one the car occupies ('left' is the FAR lane, 'right' the near
    // one — see laneLogic.mjs); it need not match `facing` while an overtake is in flight,
    // which is the whole point of the lesson's lane rule.
    this.lane = 'right';
    this.laneChanging = false;
    this._laneChanged = false;  // …and it STARTED during the update() that just ran
    this._laneTween = null;
    this._prevVert = null;      // last frame's up/down input, so we can spot a rising edge
    this._lastBeep = 0;         // when the reversing bleep last sounded
    this._uturnBtn = null;      // the DOM „Zawróć ↩︎" button, alive only while driving

    this.misfuel = null;        // { fromM } — the trip metre the wrong fuel went in at
    this._dying = false;        // the misfuelled engine is giving up: input is ours now
    this._sputter = 1;          // current fraction of the throttle that reaches the wheels
    this._sputterAt = 0;
    this._stopResolve = null;   // resolves when the forced roll-to-a-stop is done
    this._stopDeadline = 0;

    this.hud = new CarHud(scene);

    this._cursors = scene.input.keyboard.createCursorKeys();
    this._keys = scene.input.keyboard.addKeys({
      A: Phaser.Input.Keyboard.KeyCodes.A,
      D: Phaser.Input.Keyboard.KeyCodes.D,
      W: Phaser.Input.Keyboard.KeyCodes.W,
      S: Phaser.Input.Keyboard.KeyCodes.S,
    });
    this._spaceHandler = () => this._onSpace();
    scene.input.keyboard.on('keydown-SPACE', this._spaceHandler);
    // U for „zawróć" — the same thing the button and the menu row do.
    this._uHandler = () => { if (!isInputLocked(this.scene)) this.uturn(); };
    scene.input.keyboard.on('keydown-U', this._uHandler);

    // Hover label shared by every parked car ("whose car is this?" — yours).
    this._tip = iconText(scene, 0, 0, 'Twoje auto', {
      fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
      backgroundColor: '#000000aa', padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 1).setDepth(LABEL_DEPTH).setVisible(false);

    scene.events.once('shutdown', () => this._onShutdown());

    this.refreshParked();
  }

  get isDriving() { return !!this.sprite && !!this.car; }

  /** Are we in the driving school's Yaris (a lesson or the practical exam)? */
  get isSchoolCar() { return isSchoolCarId(this.car?.id); }

  /** The lesson/exam this drive belongs to — `{ route, kind, lesson? }` — or null. */
  get lesson() { return this._lesson; }

  // True for exactly one frame: the one whose update() started a U-turn. Read it AFTER
  // `update(dt)` has run (that's when the route director evaluates the frame) — it is
  // cleared at the START of the next update, not at the end of the one that set it, so a
  // consumer running after the controller still sees it.
  get uturnedThisFrame() { return this._uturned; }

  // The same contract for the lane change: true for exactly the one frame whose update()
  // STARTED it (not for the 600 ms it then takes to play out — that is `laneChanging`),
  // and cleared at the start of the next update, so a consumer running after the
  // controller still sees it.
  get laneChangedThisFrame() { return this._laneChanged; }

  /** May the car turn round right now? Standing still, in one lane, wheel in your hands. */
  get canUturn() {
    if (!this.isDriving || this._busy || this._dying || this._inputLocked) return false;
    if (isInputLocked(this.scene)) return false;
    const d = this.drive;
    return d.v === 0 && !d.reversing && !d.uturning && !this.laneChanging;
  }

  // True while a park/rescue round-trip is in flight. Every action writes the WHOLE state
  // JSONB, so the scene must not fire an overlapping one (see the reportLocation guard in
  // OutsideScene) or the later commit silently reverts this one.
  get isBusy() { return this._busy; }

  /** How many people are riding along in the car we're driving. */
  get passengerCount() { return this._passengers; }

  // The street pipes the server's `car_passengers` for OUR car through here: it's the
  // only source (a rider's own `move` comes back as a walk) for the dashboard badge, the
  // "throw them out" menu row and the door block.
  setPassengers(n, characters = [], looks = []) {
    // The instructor is not on the server's crew list (nobody can board the school car), so
    // her seat is a client-side constant: the school car always carries exactly her.
    this._passengers = this.isSchoolCar ? 1 : Math.max(0, n | 0);
    this.hud.setPassengers(this._passengers);
    this._riderChars = characters; this._riderLooks = looks;
    this.refreshOccupants();
  }

  // The windows: me at the wheel, the riders behind, each in their look (again after I change mine mid-drive).
  refreshOccupants() {
    if (!this.sprite || this.isSchoolCar) return;
    setCarOccupants(this.sprite, [myCharacter(), ...this._riderChars], { looks: [window.__myLook ?? null, ...this._riderLooks] });
  }

  // --- state plumbing -------------------------------------------------------------------

  _owned() { return gameState.raw?.cars?.owned ?? []; }
  _record(carId) { return this._owned().find(c => c.id === carId) ?? null; }
  // The school's Yaris is deliberately kept out of `catalog.cars` (it must never show up in
  // the dealership or the garage), so its spec — same keys — arrives as `catalog.school_car`.
  _spec(model) {
    const cat = gameState.raw?.catalog;
    if (model === SCHOOL_CAR_MODEL) return cat?.cars?.[model] ?? cat?.school_car ?? null;
    return cat?.cars?.[model] ?? null;
  }
  get spec() { return this.car ? this._spec(this.car.model) : null; }

  // Called by the scene whenever gameState emits: our parked cars may have moved, been
  // sold or garaged, and a refused drive-report leaves us waiting for the true tank level.
  onState() {
    if (this.isDriving) {
      const rec = this._record(this.car.id);
      if (rec) {
        // Not `_odometerBase`: the fresh record already contains the metres we reported,
        // and the HUD adds `metersThisTrip` on top of the base itself.
        this.car = rec;
        if (this._resyncFuel) {
          this._resyncFuel = false;
          this.fuelLocal = rec.fuel ?? this.fuelLocal;
        }
      }
    }
    this.refreshParked();
  }

  // --- parked cars ----------------------------------------------------------------------

  // Draw every car of ours standing on this street (minus the one we're driving), as the
  // side silhouette in its lane. Reuses sprites across calls so a state emit doesn't blink.
  refreshParked() {
    if (!this._showParked) return;
    const wanted = new Map();
    for (const c of this._owned()) {
      if (isSchoolCarId(c.id)) continue;   // the school car is nobody's and never parks
      if (c.location?.scene !== 'street') continue;
      if (this.isDriving && c.id === this.car.id) continue;
      wanted.set(c.id, c);
    }
    for (const [id, cont] of [...this._parked]) {
      if (!wanted.has(id) || cont.carModel !== wanted.get(id).model) {
        cont.destroy();
        this._parked.delete(id);
      }
    }
    for (const [id, c] of wanted) {
      const face = c.location.facing === 'left' ? 'left' : 'right';
      let cont = this._parked.get(id);
      if (!cont) {
        cont = createCarSprite(this.scene, c.model, 'side', c.location.x, 0,
          { plate: c.plate, wheelY: ROAD_WHEEL_Y[face] });
        this._parked.set(id, cont);
      }
      // The two lanes are at different depths in the perspective, so a car that changed
      // lanes changes size: re-fit the body (and with it the click box) every time.
      setCarGroundY(cont, ROAD_WHEEL_Y[face]);
      cont.x = c.location.x;
      cont.setDepth(ROAD_WHEEL_Y[face]);
      setCarFacing(cont, face);
      this._makeParkedInteractive(cont, id);
    }
    this._tip.setVisible(false);
  }

  // A Container has no texture to derive a hit area from, so give it the body's box. Called
  // again whenever the car is re-fitted (it changes size with the lane it stands in), so the
  // handlers are attached once and only the rectangle is refreshed afterwards.
  _makeParkedInteractive(cont, carId) {
    if (cont.input?.hitArea) {
      fitCarHitArea(cont);
      return;
    }
    fitCarHitArea(cont, { useHandCursor: true });
    cont.on('pointerover', () => this._tip
      .setPosition(cont.x, cont.y - (cont.img?.displayHeight ?? 150) / 2 - 6).setVisible(true));
    cont.on('pointerout', () => this._tip.setVisible(false));
    cont.on('pointerup', () => {
      if (this.isDriving) return;
      if (this._nearestParkedId() === carId) this.enter(carId);
      else showToast(this.scene, 'Podejdź bliżej do auta {car}');
    });
  }

  _nearestParkedId() {
    let best = null, bestD = Infinity;
    for (const [id, cont] of this._parked) {
      const d = Phaser.Math.Distance.Between(this.natalka.x, this.natalka.y, cont.x, cont.y);
      if (d <= NEAR && d < bestD) { best = id; bestD = d; }
    }
    return best;
  }

  // --- getting in and out ---------------------------------------------------------------

  /** Get into one of your cars that is standing on this street. */
  enter(carId) {
    if (this.isDriving || !this._canDrive()) return;
    const rec = this._record(carId);
    if (!rec || rec.location?.scene !== 'street') return;
    if (rec.broken) { showToast(this.scene, messageFor({ detail: { error_code: 'CAR_BROKEN' } })); return; }
    this._startDrive(rec, rec.location.x, rec.location.facing === 'left' ? -1 : 1);
  }

  /** The scene was entered with `{ drivingCarId }` — we drove in already sitting in it. */
  startFrom(carState) {
    if (!carState || this.isDriving) return;
    // Only ever a car the state snapshot knows about: a caller-supplied record would put
    // us behind the wheel of something the server has no drive lock on.
    const rec = this._record(typeof carState === 'string' ? carState : carState.id);
    if (!rec) return;
    const x = rec.location?.x ?? STREET_SPAWNS.HOUSE;
    this._startDrive(rec, x, rec.location?.facing === 'right' ? 1 : -1);
  }

  /**
   * Behind the wheel of the driving school's Yaris, for the lesson (or exam) in `lesson`.
   * Not a car of ours: it exists only for as long as this drive lasts, so there is no
   * record to look up — we build one (schoolCarRecord) and drive it.
   */
  startSchoolCar({ x = 0, facing = 'left', lane = null, lesson = null } = {}) {
    if (this.isDriving) return;
    this._lesson = lesson;
    this._startDrive(schoolCarRecord(window.__myId), x, facing === 'left' ? -1 : 1);
    // Which lane to start in. Facing already picks the one you belong in, so this only ever
    // matters where a caller wants the other one — the route asks for it explicitly rather
    // than trusting that default to stay what it is.
    if (lane && this.isDriving && lane !== this.lane) this._placeInLane(lane);
  }

  // Put the car in `lane` at once, with no animation — the start of a drive, not a change.
  _placeInLane(lane) {
    this.lane = lane === 'left' ? 'left' : 'right';
    setCarGroundY(this.sprite, ROAD_WHEEL_Y[this.lane]);
    this.sprite.setDepth(ROAD_WHEEL_Y[this.lane]);
  }

  /** Step out of the school car and make it disappear — it never stays at the kerb. */
  leaveSchoolCar() {
    if (!this.isSchoolCar) return;
    this._lesson = null;
    this._leaveCar({ adopt: false });
  }

  _startDrive(rec, x, facing) {
    this.car = rec;
    if (!this.isSchoolCar) this._lesson = null;   // a normal drive is never a lesson
    this.setPassengers(0);   // a fresh drive starts empty; the server says when it isn't
    this.drive = createDriveState(facing);
    this.fuelLocal = rec.fuel ?? 0;
    this.metersThisTrip = 0;
    // Snapshot the odometer once: every drive-report we send pushes the server's count up
    // by the metres we already have in `metersThisTrip`, so reading `car.odometer_m` live
    // would count this trip twice on the HUD.
    this._odometerBase = rec.odometer_m ?? 0;
    this._metersSinceReport = 0;
    this._reportTimer = 0;
    this._reportsStopped = false;
    this._resyncFuel = false;
    // A misfuel outlives a parking: the server remembers the odometer it happened at, so a
    // car left coughing at the kerb picks the sputter straight back up where it stopped.
    const misfueledAt = rec.misfueled_at_m;
    this.misfuel = misfueledAt == null
      ? null
      : { fromM: -Math.max(0, (rec.odometer_m ?? 0) - misfueledAt) };
    this._dying = false;
    this._sputter = 1;
    this._sputterAt = 0;
    this._stopResolve = null;
    this._clearOppPress();
    this._prevAxis = 0;
    this._pitch = 0;
    this._inputLocked = false;
    this._uturned = false;
    this._uturnTween = null;
    this._laneChanged = false;
    this.laneChanging = false;
    this._laneTween = null;
    this._prevVert = null;
    this._lastBeep = 0;

    // The car we're climbing into stops being scenery.
    this._parked.get(rec.id)?.destroy();
    this._parked.delete(rec.id);
    this._tip.setVisible(false);

    const face = facingStr(facing);
    this.lane = correctLane(face);   // you start in the lane your bonnet points down
    this.sprite = createCarSprite(this.scene, rec.model, 'side',
      clamp(x, EDGE_MARGIN, this.worldWidth - EDGE_MARGIN), carLaneY(rec.model, face),
      { plate: rec.plate, wheelY: ROAD_WHEEL_Y[face],
        driver: myCharacter(), looks: [window.__myLook ?? null] });
    setCarFacing(this.sprite, face);
    this.sprite.setDepth(ROAD_WHEEL_Y[face]);

    this.natalka.setVisible(false);
    this.hud.show();
    // The school car's tank never moves — a gauge that always reads 100% is just noise.
    this.hud.setFuelVisible(!this.isSchoolCar);
    this.hud.setLimit(this._speedLimit);
    this.hud.setReversing(false);
    this._uturnBtn ??= new UturnButton(this.scene, { onPress: () => this.uturn() });
    this._uturnBtn.setVisible(false);
    this._pushHud();
    this._wasMoving = false;
    this._broadcast();
    this._onEnter?.(this.sprite);
  }

  /** Leave the car where it stands. */
  async parkHere() {
    if (!this.isDriving || this._busy || this.isSchoolCar) return;   // the school car never parks
    const { id } = this.car;
    const x = Math.round(this.sprite.x);
    const face = facingStr(this.drive.facing);
    this._busy = true;
    try {
      await this._sendReport();
      this._leaveCar({ adopt: true, x, face });
      await gameState.carsPark(id, x, face);
    } catch (e) {
      showToast(this.scene, messageFor(e));
    } finally {
      this._busy = false;
      this.refreshParked();
    }
  }

  /**
   * Put it away at home — from the kerb in front of the house, or (`fromEdge`) by driving
   * off the street's right end, which IS the way home. Resolves true once the car is in
   * the garage.
   */
  async parkGarage({ fromEdge = false } = {}) {
    if (!this.isDriving || this._busy || this.isSchoolCar) return false;   // …and has no garage of ours
    if (!fromEdge && Math.abs(this.sprite.x - STREET_SPAWNS.HOUSE) > HOUSE_PARK_RANGE) {
      showToast(this.scene, 'Podjedź pod dom, żeby wjechać do garażu {yard}');
      return false;
    }
    const { id } = this.car;
    this._busy = true;
    try {
      await this._sendReport();
      this._leaveCar({ adopt: false });
      await gameState.carsParkGarage(id);
      return true;
    } catch (e) {
      showToast(this.scene, messageFor(e));
      return false;
    } finally {
      this._busy = false;
      this.refreshParked();
    }
  }

  // Step out: the character reappears beside the car, the camera goes back to her, and the
  // room is told we're a pedestrian again. `adopt` keeps the body on screen as a parked car
  // (street parking) instead of removing it (into the garage).
  _leaveCar({ adopt, x, face }) {
    const sprite = this.sprite;
    // Drop the pitch / U-turn tweens before the body changes owner (or dies) — a live
    // tween would keep writing `angle`/`y` onto a parked or destroyed car.
    this.scene.tweens.killTweensOf(sprite);
    // …and the U-turn's and the lane change's, which drive a proxy, not the sprite.
    this._uturnTween?.remove();
    this._uturnTween = null;
    this._laneTween?.remove();
    this._laneTween = null;
    this.laneChanging = false;
    const carId = this.car.id;
    const carX = sprite.x, carY = sprite.y;
    const carHalfW = (sprite.img?.displayWidth ?? 300) / 2;
    const dir = this.drive.facing;

    this.sprite = null;
    this.car = null;
    this.drive = null;
    this._inputLocked = false;
    this.setPassengers(0);
    this.hud.setReversing(false);
    this.hud.hide();
    this._uturnBtn?.setVisible(false);

    if (adopt) {
      setCarGroundY(sprite, ROAD_WHEEL_Y[face]);
      sprite.x = x;
      sprite.setAngle(0).setDepth(ROAD_WHEEL_Y[face]);
      setCarFacing(sprite, face);
      this._makeParkedInteractive(sprite, carId);
      this._parked.set(carId, sprite);
    } else {
      sprite.destroy();
    }

    // Stand clear of the bodywork — half the car plus a step — on whichever side is inside
    // the world, and a little further up the road than the lane itself: sharing the car's
    // exact y makes the depth sort a coin toss and drops her inside the car.
    const aside = carHalfW + EXIT_GAP;
    let nx = carX + aside * dir;
    if (nx < EDGE_MARGIN || nx > this.worldWidth - EDGE_MARGIN) nx = carX - aside * dir;
    nx = clamp(nx, EDGE_MARGIN, this.worldWidth - EDGE_MARGIN);
    const ny = this._walkY?.() ?? (carY - EXIT_Y_LIFT);
    this.natalka.setPosition(nx, ny).setVisible(true);

    this._wasMoving = false;
    this._onBroadcast?.({ x: nx, y: ny, facing: facingStr(dir), mode: 'walk' });
    this._onExit?.(this.natalka);
  }

  /** Roadside help: a canister (or a charge) when the tank ran dry mid-street. */
  async rescue() {
    if (!this.isDriving || this._busy || this.isSchoolCar) return;
    const { id } = this.car;
    this._busy = true;
    try {
      await gameState.carsRescue(id);
      const rec = this._record(id);
      if (rec) { this.car = rec; this.fuelLocal = rec.fuel ?? this.fuelLocal; }
      showToast(this.scene, 'Pomoc drogowa dolała paliwa {fuel}');
    } catch (e) {
      showToast(this.scene, messageFor(e));
    } finally {
      this._busy = false;
    }
  }

  // --- the pump -------------------------------------------------------------------------

  /** Stopped within reach of one of the station's pumps? */
  _atPump() {
    if (!this.isDriving) return false;
    // The instructor fuels her own car; a lesson is not a trip to the petrol station.
    if (this.isSchoolCar) return false;
    // An EV charges at home, so the pump has nothing to sell it — and saying so with a
    // toast is not worth losing the menu over: standing on the forecourt in an electric
    // car with a flat battery would otherwise replace „Auto 🚗" (and with it „Wezwij
    // pomoc") by a fuel button that can only ever refuse. Soft-lock, right by the pumps.
    if (this.spec?.fuel === 'ev') return false;
    if (this.drive.v > 0 || this.drive.reversing || this.drive.uturning) return false;
    return PUMPS.some((p) => Math.abs(this.sprite.x - p.x) <= PUMP_RANGE);
  }

  /** Fill her up. Whether that was the RIGHT nozzle is your problem, not the station's. */
  async openFuel() {
    if (!this.isDriving || this._busy || this._dying || !this._atPump()) return;
    if (this.isSchoolCar) return;
    const { id } = this.car;
    this._busy = true;
    try {
      // Hand the server the metres first: it stamps `misfueled_at_m` with the odometer as
      // it stands, and a stale one would put the 100 m mark behind us.
      await this._sendReport();
      // The record's tank is as old as that report; `fuelLocal` is what the HUD has been
      // showing, so that is what the pump quotes.
      const out = await showFuelModal(this.scene, { ...this.car, fuel: this.fuelLocal },
        gameState.raw?.catalog);
      if (!out?.refuel || !this.isDriving) return;
      const rec = this._record(id);
      if (rec) { this.car = rec; this.fuelLocal = rec.fuel ?? this.fuelLocal; }
      if ((out.refuel.liters ?? 0) <= 0) {
        // The pump ran the numbers and came back with nothing — an empty wallet, or a
        // tank that filled up between opening the modal and pressing the button. Not a
        // misfuel either: the server only ruins an engine when something went in.
        showToast(this.scene, 'Nie zatankowano nic {fuel}');
      } else if (out.refuel.misfueled) {
        this.misfuel = { fromM: this.metersThisTrip };
        showToast(this.scene, 'Zatankowano… coś dziwnie brzmi silnik {face_think}');
      } else {
        showToast(this.scene, `Zatankowano ${litres(out.refuel.liters)} za ${out.refuel.cost} zł {fuel}`);
      }
    } catch (e) {
      showToast(this.scene, messageFor(e));
    } finally {
      this._busy = false;
    }
  }

  // The wrong fuel finally kills it: roll to a stop, then tell the server — which only
  // believes a break-down once its own odometer shows the 100 m, hence the report first.
  async _breakDown() {
    if (this._dying || !this.isDriving) return;
    this._dying = true;
    this._busy = true;
    const { id } = this.car;
    try {
      await this._coastToStop();
      if (!this.isDriving) return;              // the scene shut down mid-roll
      await this._sendReport();
      let err = await this._breakDownOnce(id);
      if (err === 'CAR_NOT_YET') {
        // The server is short of metres — almost always a report it refused earlier.
        // Push what we're still holding and ask once more.
        this._reportsStopped = false;
        await this._sendReport();
        err = await this._breakDownOnce(id);
      }
      if (err) {
        // Don't hammer the endpoint every frame: the server still remembers the misfuel,
        // so the engine will die on the next drive instead.
        this.misfuel = null;
        showToast(this.scene, messageFor({ detail: { error_code: err } }));
        return;
      }
      if (!this.isDriving) return;
      this.misfuel = null;
      this._leaveCar({ adopt: false });          // towed away — no wreck left at the kerb
      showToast(this.scene, 'Silnik zgasł – zatankowałeś złe paliwo. Auto odholowane do garażu {truck}', 4000);
    } finally {
      this._dying = false;
      this._busy = false;
      this.refreshParked();
    }
  }

  async _breakDownOnce(carId) {
    try {
      await gameState.carsBreakDown(carId);
      return null;
    } catch (e) {
      return e?.detail?.error_code ?? e?.error_code ?? 'UNKNOWN';
    }
  }

  // Resolves once the car has actually stopped: update() forces the brake while `_dying`,
  // and the deadline covers an engine that let go at 200 km/h.
  _coastToStop() {
    return new Promise((resolve) => {
      if (!this.drive || this.drive.v <= 0) { resolve(); return; }
      this._stopResolve = resolve;
      this._stopDeadline = performance.now() + DIE_BRAKE_MS;
    });
  }

  // --- the menu -------------------------------------------------------------------------

  async openMenu() {
    if (!this.isDriving || this._busy || this._dying || this._inputLocked) return;
    if (this.drive.v > 0 || this.drive.reversing || this.drive.uturning) return;
    if (this.laneChanging) return;
    if (this.isSchoolCar) { this._openSchoolMenu(); return; }
    const nearHouse = Math.abs(this.sprite.x - STREET_SPAWNS.HOUSE) <= HOUSE_PARK_RANGE;
    const rescueCost = gameState.raw?.catalog?.fuel?.rescue_cost ?? 200;
    // Turning round CANNOT happen from inside the row's own handler: showActionsModal
    // awaits it while the modal still holds the scene's input lock, which `uturn()` (quite
    // rightly) refuses to drive through. So the row only names what was picked, and we act
    // on it once the modal has closed and given the wheel back.
    const actions = [
      { label: UTURN_LABEL, fn: () => 'uturn' },
      { label: 'Zaparkuj tutaj', fn: () => this.parkHere() },
      nearHouse
        ? { label: 'Do garażu', fn: () => this.parkGarage() }
        : { label: 'Do garażu (za daleko)', disabled: true },
    ];
    if (this._passengers > 0) {
      actions.push({ label: LABEL_KICK, fn: () => this._onKick?.() });
    }
    if (this.fuelLocal <= 0) {
      actions.push({ label: `Wezwij pomoc — ${rescueCost} zł`, fn: () => this.rescue() });
    }
    const picked = await showActionsModal(this.scene, {
      title: this.car.name_pl ?? 'Auto {car}',
      body: this.car.plate ? `${this.car.plate}` : 'Co robimy?',
      actions,
    });
    if (picked === 'uturn') this.uturn();
  }

  // The school car has nowhere to be left and nothing to be filled with: the only thing you
  // can do from behind its wheel is give up on the lesson (or the exam — which costs you the
  // attempt, hence the confirmation). What that MEANS is the scene's call: `onAbortLesson`.
  async _openSchoolMenu() {
    const kind = this._lesson?.kind === 'exam' ? 'exam' : 'lesson';
    const menu = abortMenu(kind);
    const picked = await showActionsModal(this.scene, {
      title: 'Nauka jazdy {car}',
      body: `${this.car?.name_pl ?? ''} · ${this.car?.plate ?? ''}`,
      actions: [
        { label: UTURN_LABEL, fn: () => 'uturn' },   // …acted on after the modal closes
        { label: menu.label, fn: () => 'abort' },
      ],
    });
    if (picked === 'uturn') { this.uturn(); return; }
    if (picked === 'abort') await this.confirmAbortLesson();
  }

  /**
   * „Przerwać lekcję?" — the confirmation on its own, without the menu around it. The route
   * scene's instruction strip has its own ✖ and asks exactly the same question.
   */
  async confirmAbortLesson() {
    if (!this.isSchoolCar) return;
    const menu = abortMenu(this._lesson?.kind === 'exam' ? 'exam' : 'lesson');
    const ok = await showModal(this.scene, {
      title: menu.title, body: menu.body, confirmLabel: menu.confirmLabel,
    });
    if (!ok || !this.isSchoolCar) return;
    this._onAbortLesson?.();
  }

  // --- input ----------------------------------------------------------------------------

  // -1 / 0 / +1 from the arrow keys, A/D, or the mobile joystick — the axis along the road.
  // Across it (up/down) is a different gesture entirely: see `_vert`.
  _axis() {
    const c = this._cursors, k = this._keys;
    const kb = ((c.left.isDown || k.A.isDown) ? -1 : 0) + ((c.right.isDown || k.D.isDown) ? 1 : 0);
    if (kb !== 0) return kb;
    const vx = virtualInput.vector.x;
    if (vx <= -AXIS_THRESHOLD) return -1;
    if (vx >= AXIS_THRESHOLD) return 1;
    return 0;
  }

  // 'up' | 'down' | null — the across-the-road axis, from ↑/↓, W/S or a decisive shove of
  // the joystick. Both keys at once is nothing: the car cannot go two ways.
  _vert() {
    const c = this._cursors, k = this._keys;
    const up = c.up.isDown || k.W.isDown;
    const down = c.down.isDown || k.S.isDown;
    if (up !== down) return up ? 'up' : 'down';
    if (up && down) return null;
    const vy = virtualInput.vector.y;
    if (vy <= -LANE_AXIS) return 'up';
    if (vy >= LANE_AXIS) return 'down';
    return null;
  }

  // One press, one lane. Read the edge every frame — even while the wheel is locked, so a
  // key that was already down when control comes back doesn't count as a fresh press.
  _pollLaneInput() {
    const dir = this._vert();
    const rising = !!dir && dir !== this._prevVert;
    this._prevVert = dir;
    if (!rising) return;
    if (this._dying || this._inputLocked || isInputLocked(this.scene)) return;
    if (this.drive.uturning) return;
    this.changeLane(dir);
  }

  /**
   * Where a scene lets the car pull down into a lay-by: `{ x0, x1 }` in world px (x0 < x1),
   * or null (the street). Set by TrainingRouteScene for its `parking` segment.
   */
  set bayZone(zone) { this._bayZone = zone ?? null; }
  get bayZone() { return this._bayZone ?? null; }

  // The lane one press of `dir` takes us to. Two road lanes everywhere; a third, 'bay', one
  // more press down, only while the car is beside a lay-by.
  _laneAfter(dir) {
    const x = this.sprite?.x ?? 0;
    const zone = this._bayZone;
    const bayOpen = !!zone && x >= zone.x0 && x <= zone.x1;
    const order = bayOpen ? ['left', 'right', 'bay'] : ['left', 'right'];
    if (this.lane === 'bay' && !bayOpen) return dir === 'up' ? 'right' : null;
    const i = order.indexOf(this.lane);
    return order[i + (dir === 'up' ? -1 : 1)] ?? null;
  }

  /**
   * Glide across to the next lane: 'up' is towards the far lane, 'down' towards the near one
   * (and, beside a lay-by, on down into it — see `bayZone`). Allowed at any speed, standstill
   * included; ignored while a change is already in flight, while the car is swinging through
   * a U-turn, or when there is no lane that way. Returns whether a change actually started.
   */
  changeLane(dir) {
    if (!this.isDriving || this.drive.uturning) return false;
    const target = this._laneAfter(dir);
    if (!target || !laneChangeAllowed({ changing: this.laneChanging, current: this.lane, target })) {
      return false;
    }
    this.lane = target;
    this.laneChanging = true;
    this._laneChanged = true;   // one frame's worth — the route rules read it after update()
    this.sprite.setDepth(wheelYForLane(target));   // the other lane sorts differently
    // Crossing the road changes the car's DEPTH in the perspective, so it changes SIZE as
    // well as y: tween the ground line the wheels are on and let setCarGroundY do both.
    const line = { y: this.sprite.groundY ?? wheelYForLane(this.lane) };
    this._laneTween?.remove();
    this._laneTween = this.scene.tweens.add({
      targets: line, y: wheelYForLane(target), duration: LANE_CHANGE_MS, ease: 'Sine.easeInOut',
      onUpdate: () => {
        if (!this.sprite) return;
        setCarGroundY(this.sprite, line.y);
        // The lane IS the y peers see, and a change at a standstill moves nothing on the x
        // axis — so the move broadcast has to come from in here, throttled like the driving
        // one, or the car would jump lanes on their screens 600 ms late.
        this._throttledBroadcast();
      },
      onComplete: () => {
        this._laneTween = null;
        this.laneChanging = false;
        this._broadcast();
      },
    });
    return true;
  }

  /**
   * Turn round on the spot — the button, the U key and the „Zawróć ↩︎" menu row all end up
   * here. Only from a standstill, and never in the middle of a lane change.
   */
  uturn() {
    if (!this.canUturn) return false;
    this._startUturn();
    return true;
  }

  // Turns the axis into a physics input, and owns the one thing physics doesn't know
  // about: reverse (hold the other way while stopped).
  //
  // What the "wrong way" key means is decided ONCE, on the frame it goes down, and holds
  // for the whole press:
  //   pressed while rolling (v > CREEP_KMH) → a brake, all the way down to a standstill,
  //     and nothing at all on release. Braking to a stop must not become reverse.
  //   pressed at a standstill → reverse, once it has been held REVERSE_HOLD_MS.
  // Hence `_oppMode`: the speed crossing 0 mid-press can never re-arm the press.
  //
  // A TAP of that key used to be the U-turn. It isn't any more — no child ever found it,
  // and it made the very same key mean two opposite things. Turning round is `uturn()`
  // now: a button you can see, the U key, or the „Zawróć ↩︎" row in the menu.
  _resolveInput(now) {
    const d = this.drive;
    const axis = this._axis();
    const prevAxis = this._prevAxis;
    this._prevAxis = axis;

    // The wrong fuel has just killed the engine: the wheel is out of your hands until the
    // car has rolled to a stop. Same treatment while the route director holds the wheel
    // (`lockInput`, at the finish line): the car brakes itself to a standstill.
    if (this._dying || this._inputLocked) { this._clearOppPress(); return 'brake'; }

    // While the U-turn plays (or a modal has the keyboard) nothing is a press: a key that
    // is still down when control comes back has to be released and pressed again.
    if (d.uturning || isInputLocked(this.scene)) { this._clearOppPress(); return 'none'; }

    const dry = this.fuelLocal <= 0;
    const opp = -d.facing;

    if (axis === d.facing) {
      this._clearOppPress();
      if (d.reversing) { d.reversing = false; d.v = 0; }
      return dry ? 'none' : 'throttle';
    }

    if (axis === opp) {
      if (prevAxis !== opp) {          // rising edge — this is where the press gets its meaning
        this._oppMode = d.v > CREEP_KMH ? 'brake' : 'reverse';
        this._oppDownSince = this._oppMode === 'reverse' ? now : null;
      }
      if (this._oppMode === 'brake') return 'brake';       // …until released, 0 km/h included
      if (d.reversing) return dry ? 'none' : 'throttle';   // still held: keep crawling back
      if (this._oppDownSince !== null && now - this._oppDownSince >= REVERSE_HOLD_MS) {
        d.reversing = true;
        return dry ? 'none' : 'throttle';
      }
      return 'none';   // still inside the hold window — nothing happens yet
    }

    // Released (or never pressed): a press too short to become reverse simply did nothing.
    this._clearOppPress();
    if (d.reversing) { d.reversing = false; d.v = 0; }
    return 'none';
  }

  /**
   * Take the wheel away from the player: steering and SPACE stop doing anything and the car
   * brakes itself to a stop (like the roll-out of a dead engine). Used by the route director
   * once the lesson is over. Lasts until `unlockInput()` or the end of the drive.
   */
  lockInput() { this._inputLocked = true; }

  /** Hand the wheel back. */
  unlockInput() { this._inputLocked = false; }

  _clearOppPress() {
    this._oppDownSince = null;
    this._oppMode = null;
  }

  _startUturn() {
    const d = this.drive;
    if (d.uturning) return;
    d.uturning = true;
    this._uturned = true;   // one frame's worth — the route director reads it after update()
    d.facing = -d.facing;
    const face = facingStr(d.facing);
    this.lane = correctLane(face);   // turning round puts you in the other lane by definition
    setCarFacing(this.sprite, face);
    this.sprite.setDepth(ROAD_WHEEL_Y[face]);   // the other lane sorts differently
    // Swinging into the other lane changes the car's DEPTH in the scene, so it changes size
    // as well as y: tween the ground line the wheels are on and let setCarGroundY do both.
    const line = { y: this.sprite.groundY ?? ROAD_WHEEL_Y[facingStr(-d.facing)] };
    this._uturnTween?.remove();
    this._uturnTween = this.scene.tweens.add({
      targets: line, y: ROAD_WHEEL_Y[face], duration: UTURN_MS, ease: 'Sine.easeInOut',
      onUpdate: () => { if (this.sprite) setCarGroundY(this.sprite, line.y); },
      onComplete: () => {
        this._uturnTween = null;
        if (this.drive) this.drive.uturning = false;
        // A U-turn at a standstill moves nothing on the x axis, so the throttled move
        // broadcast never fires — peers would keep the car in the old lane (and, with the
        // perspective, at the old size) until it drives off. Tell them once, now.
        this._broadcast();
      },
    });
  }

  _setPitch(target) {
    if (this._pitch === target) return;
    this._pitch = target;
    this.scene.tweens.add({ targets: this.sprite, angle: target, duration: PITCH_MS, ease: 'Sine.easeOut' });
  }

  // --- the frame ------------------------------------------------------------------------

  update(deltaMs) {
    this._uturned = false;
    this._laneChanged = false;
    if (!this.isDriving) return;
    const spec = this.spec;
    if (!spec) return;                       // catalog not loaded yet — skip the frame entirely
    const dt = Math.min(deltaMs, 100) / 1000;   // a tab that was backgrounded must not teleport
    const now = performance.now();

    const input = this._resolveInput(now);
    this._pollLaneInput();
    // A misfuelled engine can't hold a throttle: re-roll how much of the pedal actually
    // reaches the wheels every SPUTTER_MS and give physics that fraction of the tick.
    // Only the throttle is scaled — braking a coughing car still has to work properly.
    if (this.misfuel && now - this._sputterAt >= SPUTTER_MS) {
      this._sputterAt = now;
      this._sputter = SPUTTER_MIN + Math.random() * (1 - SPUTTER_MIN);
    }
    const driveDt = this.misfuel && input === 'throttle' ? dt * this._sputter : dt;
    this.drive = step(this.drive, input, driveDt, spec);

    // Move. Reverse is a fixed crawl backwards; forward is the (visually capped) speed.
    // In reverse the physics pins v to REVERSE_KMH while the key is held and 0 otherwise,
    // so the same expression covers "backing up" and "out of fuel, going nowhere".
    const before = this.sprite.x;
    const dx = this.drive.reversing
      ? -this.drive.facing * this.drive.v * PX_PER_KMH * dt
      : this.drive.facing * pxPerSec(this.drive.v) * dt;
    this.sprite.x = clamp(before + dx, EDGE_MARGIN, this.worldWidth - EDGE_MARGIN);
    if (!this.drive.uturning && !this.laneChanging) {
      // The LANE, not the facing: overtaking means being on the other side of the road for
      // a while, and this line used to drag the car straight back the moment it got there.
      const laneY = ROAD_WHEEL_Y[this.lane];
      // Only re-fit when the lane actually changed (the tweens did that already); every
      // other frame just undoes the previous one's jitter.
      if (this.sprite.groundY !== laneY) setCarGroundY(this.sprite, laneY);
      else this.sprite.y = laneY - this.sprite.wheelOffset;
    }
    // …and it shakes on its mounts while it coughs.
    if (this.misfuel && !this.drive.uturning && !this.laneChanging && this.drive.v > 0) {
      this.sprite.y += (Math.random() * 2 - 1) * SPUTTER_JITTER_PX;
    }

    // Burn what we actually covered, not what the speedo claims (they differ above the
    // visual speed cap) — the server's guard is metre-based too.
    const metres = Math.abs(this.sprite.x - before) / PX_PER_M;
    if (metres > 0) {
      this.metersThisTrip += metres;
      // The school's Yaris burns nothing and owes nothing: no tank to drain, no odometer
      // the server keeps — the lesson is scored on where you drove, not on how far.
      if (!this.isSchoolCar) {
        this._metersSinceReport += metres;
        let fuel = this.fuelLocal - fuelBurn(metres, this.drive.v, spec);
        if (input === 'none') fuel += regen(metres, spec, this.drive.v);
        this.fuelLocal = clamp(fuel, 0, spec.capacity);
      }
    }

    this._setPitch(input === 'brake' ? PITCH_BRAKE : input === 'throttle' ? PITCH_THROTTLE : 0);
    this._pushHud();
    this._reverseFeedback(now);
    // Turning round is only ever offered at a standstill, so the button is polled here
    // rather than on a timer of its own — this frame already knows everything it needs.
    this._uturnBtn?.setVisible(this.canUturn);

    // The engine's last hundred metres, then the forced roll to a standstill.
    if (this._stopResolve && (this.drive.v <= 0 || now >= this._stopDeadline)) {
      const done = this._stopResolve;
      this._stopResolve = null;
      this.drive.v = 0;
      done();
    } else if (this.misfuel && !this._dying
      && this.metersThisTrip - this.misfuel.fromM >= MISFUEL_DIE_AFTER_M) {
      // Fire-and-forget from a frame callback: an unhandled rejection here would surface
      // as a global error, and there is no caller left to catch it.
      this._breakDown().catch(() => { this._dying = false; this._busy = false; });
    }

    // Peers: 10 Hz while we're moving, plus one final frame so they see us stop.
    const moving = this.drive.v > 0;
    if (moving) {
      if (now - this._lastBroadcast >= BROADCAST_MS) { this._lastBroadcast = now; this._broadcast(); }
      this._wasMoving = true;
    } else if (this._wasMoving) {
      this._wasMoving = false;
      this._broadcast();
    }

    this._reportTimer += deltaMs;
    // Never overlap a report with a refuel/park: both write the whole state JSONB.
    if (this._reportTimer >= REPORT_EVERY_MS && !this._busy && !this.isSchoolCar) {
      this._reportTimer = 0;
      this._sendReport();
    }
  }

  // „R" on the dashboard and a bleep once a second, so backing up is unmistakable — both
  // to the child holding the key down and to anyone watching over their shoulder.
  _reverseFeedback(now) {
    const rev = !!this.drive.reversing;
    this.hud.setReversing(rev);
    if (!rev) { this._lastBeep = 0; return; }
    if (now - this._lastBeep < BEEP_EVERY_MS) return;
    this._lastBeep = now;
    this.hud.beep();
  }

  /** Stop the car dead at `x` — the scene uses this to hold it inside the street. */
  stopAt(x) {
    if (!this.isDriving) return;
    this.sprite.x = x;
    this.drive.v = 0;
    this.drive.reversing = false;
  }

  /** The sign the HUD shows from now on, in km/h — `null` for a road with no limit. */
  setSpeedLimit(kmh) {
    this._speedLimit = Number.isFinite(kmh) && kmh > 0 ? kmh : null;
    this.hud.setLimit(this._speedLimit);
  }

  _pushHud() {
    const spec = this.spec;
    if (!spec) return;
    const ev = spec.fuel === 'ev';
    this.hud.update({
      speed: this.drive.v,
      fuelPct: spec.capacity > 0 ? (this.fuelLocal / spec.capacity) * 100 : 0,
      ev,
      km: (this._odometerBase + this.metersThisTrip) / 1000,
    });
  }

  // The 10 Hz move broadcast, for the things that move the car outside the driving loop
  // (the lane tween). Same budget as update()'s, from the same clock.
  _throttledBroadcast() {
    const now = performance.now();
    if (now - this._lastBroadcast < BROADCAST_MS) return;
    this._lastBroadcast = now;
    this._broadcast();
  }

  _broadcast() {
    if (!this.isDriving) return;
    this._onBroadcast?.({
      x: this.sprite.x, y: this.sprite.y,
      facing: facingStr(this.drive.facing),
      mode: 'car',
      car_id: this.car.id,
      car_model: this.car.model,
      speed: Math.round(this.drive.v),
    });
  }

  // Hand the server the metres covered since the last report and the tank we have left.
  // Best-effort: a refusal (a bad odometer, a car the server thinks is parked) stops the
  // reporting for this drive rather than beeping once a second, and marks the tank for a
  // re-read from the next state snapshot.
  async _sendReport() {
    // Nothing to report for the school car: the server keeps no odometer or tank for it.
    if (!this.car || this._reportsStopped || this.isSchoolCar) return;
    const metres = this._metersSinceReport;
    if (metres <= 0) return;
    this._metersSinceReport = 0;
    const err = await gameState.carsDriveReport(this.car.id, metres, this.fuelLocal);
    if (err) {
      this._reportsStopped = true;
      this._resyncFuel = true;
    }
  }

  // --- mobile action button -------------------------------------------------------------

  /** Register/clear the shared action. Returns true when it now holds one of ours. */
  syncAction() {
    if (isInputLocked(this.scene) || this._inputLocked) {
      this._clearAction();
      this._uturnBtn?.setVisible(false);
      return false;
    }
    if (this.isDriving) {
      if (this._dying || this._busy) { this._clearAction(); return false; }
      if (this._atPump()) {
        virtualInput.registerAction(LABEL_FUEL, () => this.openFuel());
        return true;
      }
      if (this.drive.v === 0 && !this.drive.reversing && !this.drive.uturning
        && !this.laneChanging) {
        virtualInput.registerAction(LABEL_DRIVE, () => this.openMenu());
        return true;
      }
      this._clearAction();
      return false;
    }
    const id = this._canDrive() ? this._nearestParkedId() : null;
    if (id) {
      virtualInput.registerAction(LABEL_ENTER, () => this.enter(id));
      return true;
    }
    this._clearAction();
    return false;
  }

  _clearAction() {
    const a = virtualInput.getAction();
    if (a && OUR_LABELS.has(a.label)) virtualInput.clearAction();
  }

  _onSpace() {
    if (isInputLocked(this.scene) || this._inputLocked) return;
    if (this.isDriving) {
      if (this._atPump()) this.openFuel(); else this.openMenu();
      return;
    }
    const id = this._nearestParkedId();
    if (id) this.enter(id);
  }

  _onShutdown() {
    this.scene.input.keyboard.off('keydown-SPACE', this._spaceHandler);
    this.scene.input.keyboard.off('keydown-U', this._uHandler);
    this._uturnBtn?.destroy();
    this._uturnBtn = null;
    this._stopResolve?.();       // a break-down mid-roll must not await a frame that never comes
    this._stopResolve = null;
    this._sendReport();          // fire-and-forget: never lose a drive's odometer
    // …and then we are no longer in a car. `_sendReport` reads `this.car.id` before its
    // first await, so nulling here is safe — and it makes `isDriving` false for anything
    // still holding a reference to a controller whose scene has gone (a pending menu
    // callback, a break-down mid-roll), which every method here guards on.
    this.sprite = null;
    this.car = null;
    this.drive = null;
    this._lesson = null;
    this._inputLocked = false;
    this.laneChanging = false;
    this._laneTween?.remove();
    this._laneTween = null;
    this._clearAction();
    for (const c of this._parked.values()) c.destroy();
    this._parked.clear();
  }
}
